import React, { useEffect, useState } from "react";
import { Alert, Button, Form, Input, Modal, Steps, Switch, Tag } from "antd";
import { CheckCircleFilled } from "@ant-design/icons";

import { channelOptions, providerLabels, providerOptions } from "../../constants";

import type {
  WebhookChannel,
  WebhookIntegration,
  WebhookIntegrationForm,
  WebhookProvider,
} from "../../types";

interface ConnectWebhookModalProps {
  open: boolean;
  integration: WebhookIntegration | null;
  isSubmitting: boolean;
  onCancel: () => void;
  onSubmit: (form: WebhookIntegrationForm) => void;
}

interface DetailsFormValues {
  name: string;
  business_phone?: string;
  waba_id?: string;
  auto_create_lead: boolean;
}

const STEP = {
  CHANNEL: 0,
  PROVIDER: 1,
  DETAILS: 2,
} as const;

type ConnectStep = (typeof STEP)[keyof typeof STEP];

const STEP_ITEMS = [
  { title: "Channel", description: "Lead source" },
  { title: "Provider", description: "Webhook sender" },
  { title: "Details", description: "Connection setup" },
];

const PHONE_PATTERN = /^\+?[0-9\s-]{10,16}$/;
const SELECTED_COLOR = "#22c55e";

const optionCardStyle = (isSelected: boolean): React.CSSProperties => ({
  cursor: "pointer",
  position: "relative",
  border: `1px solid ${isSelected ? SELECTED_COLOR : "#e5e7eb"}`,
  background: isSelected ? "#f0fdf4" : "#ffffff",
  boxShadow: isSelected ? "0 0 0 3px rgba(34, 197, 94, 0.12)" : "none",
  borderRadius: 12,
  padding: "14px 16px",
  transition: "border-color 0.2s, box-shadow 0.2s",
});

interface OptionCardProps {
  icon: string;
  label: string;
  description: string;
  isSelected: boolean;
  onSelect: () => void;
}

const OptionCard: React.FC<OptionCardProps> = ({
  icon,
  label,
  description,
  isSelected,
  onSelect,
}) => (
  <div
    role="radio"
    aria-checked={isSelected}
    tabIndex={0}
    style={optionCardStyle(isSelected)}
    onClick={onSelect}
    onKeyDown={(event) => (event.key === "Enter" || event.key === " ") && onSelect()}>
    <div className="d-flex align-items-center gap-3">
      <span
        className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
        style={{ width: 40, height: 40, background: "#ecfdf5" }}>
        <i className={`${icon} fs-20 text-success`} />
      </span>
      <div className="flex-grow-1">
        <div className="fw-semibold">{label}</div>
        <small className="text-muted">{description}</small>
      </div>
      {isSelected && <CheckCircleFilled style={{ color: SELECTED_COLOR, fontSize: 18 }} />}
    </div>
  </div>
);

interface StepHeadingProps {
  title: string;
  subtitle: string;
}

const StepHeading: React.FC<StepHeadingProps> = ({ title, subtitle }) => (
  <div className="mb-3">
    <h6 className="fw-semibold mb-1">{title}</h6>
    <small className="text-muted">{subtitle}</small>
  </div>
);

export const ConnectWebhookModal: React.FC<ConnectWebhookModalProps> = ({
  open,
  integration,
  isSubmitting,
  onCancel,
  onSubmit,
}) => {
  const [form] = Form.useForm<DetailsFormValues>();
  const [step, setStep] = useState<ConnectStep>(STEP.CHANNEL);
  const [channel, setChannel] = useState<WebhookChannel | null>(null);
  const [provider, setProvider] = useState<WebhookProvider | null>(null);
  const isEditMode = Boolean(integration);

  useEffect(() => {
    if (!open) return;
    if (integration) {
      setChannel(integration.channel);
      setProvider(integration.provider);
      setStep(STEP.DETAILS);
      form.setFieldsValue({
        name: integration.name,
        business_phone: integration.business_phone || "",
        waba_id: integration.waba_id || "",
        auto_create_lead: integration.auto_create_lead,
      });
      return;
    }
    setChannel(null);
    setProvider(null);
    setStep(STEP.CHANNEL);
    form.resetFields();
  }, [open, integration, form]);

  const goToDetails = (value: WebhookProvider) => {
    setStep(STEP.DETAILS);
    if (!form.getFieldValue("name")) {
      form.setFieldsValue({
        name: `${providerLabels[value]} WhatsApp`,
        auto_create_lead: true,
      });
    }
  };

  const handleSelectChannel = (value: WebhookChannel) => {
    if (value !== channel) setProvider(null);
    setChannel(value);
    setStep(STEP.PROVIDER);
  };

  const handleSelectProvider = (value: WebhookProvider) => {
    setProvider(value);
    goToDetails(value);
  };

  const canGoNext =
    (step === STEP.CHANNEL && Boolean(channel)) || (step === STEP.PROVIDER && Boolean(provider));

  const handleNext = () => {
    if (step === STEP.CHANNEL && channel) setStep(STEP.PROVIDER);
    if (step === STEP.PROVIDER && provider) goToDetails(provider);
  };

  const handleBack = () => {
    if (step === STEP.DETAILS) setStep(STEP.PROVIDER);
    if (step === STEP.PROVIDER) setStep(STEP.CHANNEL);
  };

  const handleStepClick = (target: number) => {
    if (target < step) setStep(target as ConnectStep);
  };

  const handleSubmit = async () => {
    if (step !== STEP.DETAILS || !channel || !provider) return;
    const values = await form.validateFields();
    onSubmit({
      name: values.name.trim(),
      channel,
      provider,
      business_phone: values.business_phone?.trim() || "",
      waba_id: values.waba_id?.trim() || "",
      auto_create_lead: values.auto_create_lead,
    });
  };

  const selectedChannelLabel = channelOptions.find((option) => option.value === channel)?.label;

  const renderChannelStep = () => (
    <>
      <StepHeading
        title="Choose a channel"
        subtitle="Select the platform your leads and chats come from."
      />
      <div className="d-flex flex-column gap-2" role="radiogroup">
        {channelOptions.map((option) => (
          <OptionCard
            key={option.value}
            icon={option.icon}
            label={option.label}
            description={option.description}
            isSelected={channel === option.value}
            onSelect={() => handleSelectChannel(option.value)}
          />
        ))}
      </div>
    </>
  );

  const renderProviderStep = () =>
    channel && (
      <>
        <StepHeading
          title="Choose a provider"
          subtitle={`Select the service that sends ${selectedChannelLabel} events to your CRM.`}
        />
        <div className="d-flex flex-column gap-2" role="radiogroup">
          {providerOptions[channel].map((option) => (
            <OptionCard
              key={option.value}
              icon="ti ti-plug-connected"
              label={option.label}
              description={option.description}
              isSelected={provider === option.value}
              onSelect={() => handleSelectProvider(option.value)}
            />
          ))}
        </div>
      </>
    );

  const renderDetailsStep = () => (
    <>
      {!isEditMode && (
        <StepHeading
          title="Connection details"
          subtitle="Name this connection and choose how new numbers are handled."
        />
      )}
      {channel && provider && (
        <div className="d-flex align-items-center gap-2 mb-3">
          <Tag color="green" className="m-0">
            <i className="ti ti-brand-whatsapp me-1" />
            {selectedChannelLabel}
          </Tag>
          <Tag className="m-0">{providerLabels[provider]}</Tag>
        </div>
      )}
      <Form form={form} layout="vertical" initialValues={{ auto_create_lead: true }}>
        <Form.Item
          name="name"
          label="Connection name"
          rules={[
            { required: true, message: "Enter a name for this connection" },
            { min: 2, max: 100, message: "Name must be 2-100 characters" },
          ]}>
          <Input placeholder="e.g. Sales WhatsApp" />
        </Form.Item>
        <div className="row">
          <div className="col-md-6">
            <Form.Item
              name="business_phone"
              label="Business number (optional)"
              rules={[{ pattern: PHONE_PATTERN, message: "Enter a valid phone number" }]}>
              <Input placeholder="e.g. 919311657253" />
            </Form.Item>
          </div>
          <div className="col-md-6">
            <Form.Item name="waba_id" label="WABA ID (optional)">
              <Input placeholder="e.g. 2010673296348226" maxLength={64} />
            </Form.Item>
          </div>
        </div>
        <small className="text-muted d-block mb-3" style={{ marginTop: -12 }}>
          If the WABA ID is left empty, it is detected from the first event and used to reject
          events from other accounts.
        </small>
        <Form.Item
          name="auto_create_lead"
          label="Create a Lead Bank lead for new numbers"
          valuePropName="checked"
          className="mb-0">
          <Switch />
        </Form.Item>
      </Form>
      {!isEditMode && provider && (
        <Alert
          className="mt-3"
          type="info"
          showIcon
          message={`After you create it, you get a webhook URL and a one-time secret header to add in ${providerLabels[provider]}.`}
        />
      )}
    </>
  );

  const renderStep = () => {
    if (step === STEP.CHANNEL) return renderChannelStep();
    if (step === STEP.PROVIDER) return renderProviderStep();
    return renderDetailsStep();
  };

  const renderFooter = () => {
    if (isEditMode) {
      return [
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button key="save" type="primary" loading={isSubmitting} onClick={handleSubmit}>
          Save
        </Button>,
      ];
    }

    return (
      <div className="d-flex justify-content-between align-items-center">
        <Button onClick={onCancel}>Cancel</Button>
        <div className="d-flex gap-2">
          {step !== STEP.CHANNEL && <Button onClick={handleBack}>Back</Button>}
          {step === STEP.DETAILS ? (
            <Button type="primary" loading={isSubmitting} onClick={handleSubmit}>
              Create Webhook
            </Button>
          ) : (
            <Button type="primary" disabled={!canGoNext} onClick={handleNext}>
              Next
            </Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <Modal
      open={open}
      title={isEditMode ? "Edit Webhook Connection" : "Connect New Webhook"}
      onCancel={onCancel}
      footer={renderFooter()}
      width={600}
      destroyOnClose>
      {!isEditMode && (
        <Steps
          size="small"
          current={step}
          onChange={handleStepClick}
          items={STEP_ITEMS.map((item, index) => ({
            ...item,
            disabled: index > step,
          }))}
          className="mt-2 mb-4"
        />
      )}
      {renderStep()}
    </Modal>
  );
};
